import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Globe2, Users, Briefcase, Plus, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { locateMe } from "@/lib/mapFocus";
import { CountriesSheet } from "./CountriesSheet";
import { TravelFriendsSheet } from "./TravelFriendsSheet";
import { AddTravelFriend } from "./AddTravelFriend";
import { LocateSheet } from "./LocateSheet";

const Tab = ({ icon: Icon, label, onClick, testId, active }) => (
  <button onClick={onClick} data-testid={testId}
    className={`flex-1 flex flex-col items-center gap-1 py-2 text-[10px] sm:text-[11px] transition-colors ${active ? "text-emerald-300" : "text-slate-400 hover:text-slate-100"}`}>
    <Icon size={19} /> <span className="truncate max-w-full">{label}</span>
  </button>
);

// Keeps a shared city fresh: at most once an hour, only if the user turned sharing on
const useRefreshLocation = () => {
  const qc = useQueryClient();
  const { data: s } = useQuery({ queryKey: ["settings"], queryFn: api.settings });
  useEffect(() => {
    if (!s?.location_share) return;
    if (s.location?.at && Date.now() - new Date(s.location.at).getTime() < 3600e3) return;
    locateMe().then((p) => api.setLocation(p)).then(() => qc.invalidateQueries({ queryKey: ["settings"] })).catch(() => null);
  }, [s?.location_share]); // eslint-disable-line react-hooks/exhaustive-deps
};

export const BottomBar = ({ data, b, geo }) => {
  const [open, setOpen] = useState(null);
  const [addTab, setAddTab] = useState("contact");
  useRefreshLocation();
  useEffect(() => {
    const show = () => { setAddTab("swap"); setOpen("add"); };
    window.addEventListener("bl:open-card", show);
    return () => window.removeEventListener("bl:open-card", show);
  }, []);
  const sheet = (k) => ({ open: open === k, onOpenChange: (o) => { setOpen(o ? k : null); if (!o) setAddTab("contact"); } });
  return (
    <>
      <nav className="fixed bottom-0 inset-x-0 z-40 pb-[env(safe-area-inset-bottom)] glass-bar border-t border-white/10 sm:bottom-4 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:w-[520px] sm:rounded-2xl sm:border sm:pb-0 shadow-2xl" data-testid="bottom-bar">
        <div className="flex items-end px-1">
          <Tab icon={Globe2} label="Countries" onClick={() => setOpen("countries")} active={open === "countries"} testId="dock-countries" />
          <Tab icon={Users} label="Contacts" onClick={() => setOpen("contacts")} active={open === "contacts"} testId="dock-contacts" />
          <div className="flex-1 flex justify-center">
            <button onClick={() => setOpen("add")} aria-label="Add travel friend" data-testid="dock-add"
              className="-mt-5 w-14 h-14 rounded-full bg-emerald-400 text-[#04120c] grid place-items-center shadow-[0_8px_30px_rgba(52,211,153,0.45)] ring-4 ring-[#0B0F17] hover:bg-emerald-300 hover:scale-105 active:scale-95 transition-[transform,background-color]">
              <Plus size={28} strokeWidth={2.5} />
            </button>
          </div>
          <Tab icon={Briefcase} label="Profession" onClick={() => setOpen("profession")} active={open === "profession"} testId="dock-profession" />
          <Tab icon={MapPin} label="Locate" onClick={() => setOpen("locate")} active={open === "locate"} testId="dock-locate" />
        </div>
      </nav>
      <CountriesSheet {...sheet("countries")} data={data} b={b} names={geo?.names || []} />
      <TravelFriendsSheet {...sheet("contacts")} mode="name" data={data} b={b} names={geo?.names || []} />
      <TravelFriendsSheet {...sheet("profession")} mode="profession" data={data} b={b} names={geo?.names || []} />
      <AddTravelFriend {...sheet("add")} tab={addTab} names={geo?.names || []} />
      <LocateSheet {...sheet("locate")} />
    </>
  );
};
